import { useMemo } from 'react';
import { FxOpenReceivable } from '@/services/financeInvoices';

/** A week of expected collections. `isOverdue` marks the single past-due bucket. */
export type CashWeek = {
  key: string;
  /** Short axis label, e.g. "Aug 10" or "Overdue". */
  label: string;
  amount: number;
  invoiceCount: number;
  cumulative: number;
  isOverdue: boolean;
};

export type OverdueInvoice = FxOpenReceivable & { daysLate: number };

export type ClientConcentration = {
  clientName: string;
  openBalance: number;
  overdue: number;
  pctOfAr: number;
  invoiceCount: number;
};

export type ReceivablesOutlook = {
  totalOpen: number;
  totalOverdue: number;
  overdueCount: number;
  /** Expected to arrive within 30 days, excluding anything already late. */
  next30Days: number;
  weeks: CashWeek[];
  overdueInvoices: OverdueInvoice[];
  byClient: ClientConcentration[];
  /** Share of AR held by the two largest clients — a concentration risk signal. */
  top2Pct: number;
};

const DAY = 86400000;

function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() - out.getDay()); // Sunday-based, matching the app's date handling
  return out;
}

function fmtWeek(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Turns open invoices into the three CFO views: when cash lands, what is late,
 * and how concentrated the risk is.
 *
 * Everything here keys off DUE DATE, which assumes clients pay on time. There is
 * no payment-date data to do better — matched_transaction_id is unset on every
 * invoice and the QuickBooks sync stores no paid date — so the UI labels this as
 * an assumption rather than a forecast.
 */
export default function useReceivablesOutlook(
  receivables: FxOpenReceivable[]
): ReceivablesOutlook {
  return useMemo(() => {
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const overdueInvoices: OverdueInvoice[] = [];
    const dated: Array<{ r: FxOpenReceivable; due: Date }> = [];

    for (const r of receivables) {
      if (!r.dueDate) {
        // No due date means no position on the timeline; treat as overdue so it
        // is chased rather than quietly dropped from the outlook.
        overdueInvoices.push({ ...r, daysLate: 0 });
        continue;
      }
      const due = new Date(`${r.dueDate}T00:00:00`);
      if (due < todayMid) {
        overdueInvoices.push({
          ...r,
          daysLate: Math.round((todayMid.getTime() - due.getTime()) / DAY),
        });
      } else {
        dated.push({ r, due });
      }
    }

    const totalOverdue =
      overdueInvoices.reduce((s, i) => s + i.openBalance, 0);
    const totalOpen = receivables.reduce((s, r) => s + r.openBalance, 0);

    // Bucket the not-yet-due invoices by the week their due date falls in.
    const buckets = new Map<string, { date: Date; amount: number; count: number }>();
    for (const { r, due } of dated) {
      const wk = startOfWeek(due);
      const key = wk.toISOString().slice(0, 10);
      const b = buckets.get(key) ?? { date: wk, amount: 0, count: 0 };
      b.amount += r.openBalance;
      b.count += 1;
      buckets.set(key, b);
    }

    const weeks: CashWeek[] = [];
    let running = 0;

    if (totalOverdue > 0) {
      running += totalOverdue;
      weeks.push({
        key: 'overdue',
        label: 'Overdue',
        amount: totalOverdue,
        invoiceCount: overdueInvoices.length,
        cumulative: running,
        isOverdue: true,
      });
    }

    for (const [key, b] of Array.from(buckets.entries()).sort(([a], [c]) => a.localeCompare(c))) {
      running += b.amount;
      weeks.push({
        key,
        label: fmtWeek(b.date),
        amount: b.amount,
        invoiceCount: b.count,
        cumulative: running,
        isOverdue: false,
      });
    }

    const in30 = todayMid.getTime() + 30 * DAY;
    const next30Days = dated
      .filter(({ due }) => due.getTime() <= in30)
      .reduce((s, { r }) => s + r.openBalance, 0);

    // Concentration: who holds the receivable, and how much of it is already late.
    const clientMap = new Map<string, ClientConcentration>();
    for (const r of receivables) {
      const c = clientMap.get(r.clientName) ?? {
        clientName: r.clientName,
        openBalance: 0,
        overdue: 0,
        pctOfAr: 0,
        invoiceCount: 0,
      };
      c.openBalance += r.openBalance;
      c.invoiceCount += 1;
      if (r.dueDate && new Date(`${r.dueDate}T00:00:00`) < todayMid) c.overdue += r.openBalance;
      clientMap.set(r.clientName, c);
    }

    const byClient = Array.from(clientMap.values())
      .map((c) => ({ ...c, pctOfAr: totalOpen > 0 ? (c.openBalance / totalOpen) * 100 : 0 }))
      .sort((a, b) => b.openBalance - a.openBalance);

    return {
      totalOpen,
      totalOverdue,
      overdueCount: overdueInvoices.length,
      next30Days,
      weeks,
      // Worst first: a small invoice 161 days late matters more than a large one 8 days late.
      overdueInvoices: overdueInvoices.sort((a, b) => b.daysLate - a.daysLate),
      byClient,
      top2Pct: byClient.slice(0, 2).reduce((s, c) => s + c.pctOfAr, 0),
    };
  }, [receivables]);
}
